import React, { memo } from "react";
import { Form, Col, FormGroup, Row } from "react-bootstrap";
import {
	Controller,
	ControllerRenderProps,
	useFormContext,
} from "react-hook-form";

import {
	DatePickerCommon,
	DatePickerWithLoaderWrapper,
	ErrorMessage,
	RequiredMark,
} from "components";

import styles from "./fields.module.scss";

type HookFormDatePickerCommonProps = {
	name: string;
	placeholderText?: string;
	label?: string;
	hideLabel?: boolean;
	isLoading?: boolean;
	defaultValue?: any;
	tabIndex?: number;
	controlId?: string;
	showRequiredMark?: boolean;
	className?: string;
	colInput?: number;
	colLabel?: number;
	rules?: any;
	minDate?: Date | null;
	maxDate?: Date | null;
	onChange?: (value?: Date | null) => void;
};

const HookFormDatePickerCommon = ({
	name,
	label,
	hideLabel,
	placeholderText,
	isLoading,
	defaultValue = "",
	tabIndex,
	controlId,
	showRequiredMark = false,
	className,
	colInput = 6,
	colLabel = 6,
	rules,
	minDate,
	maxDate,
	onChange,
}: HookFormDatePickerCommonProps) => {
	const {
		control,
		formState: { errors },
		setValue,
		trigger,
	} = useFormContext();

	const getErrorByFieldName = (errors, fieldName) => {
		return fieldName
			.split(".")
			.reduce((acc, key) => (acc ? acc[key] : undefined), errors);
	};

	return (
		<FormGroup
			as={Row}
			className={className || ""}
			controlId={controlId || name}
		>
			{!hideLabel && (
				<Form.Label column sm={colLabel}>
					{label}
					{showRequiredMark && <RequiredMark />}
				</Form.Label>
			)}
			<Col sm={colInput}>
				<Controller
					control={control}
					name={name}
					defaultValue={defaultValue}
					render={({ field }: { field: ControllerRenderProps }) => (
						<DatePickerWithLoaderWrapper
							input={field}
							isLoading={!!isLoading}
							renderDatePicker={input => {
								const isError = !!getErrorByFieldName(
									errors,
									input?.name,
								);

								return (
									<DatePickerCommon
										value={input?.value}
										className={
											isError ? styles.inputError : ""
										}
										onChange={(value, e) => {
											input?.onChange(value, e);
											trigger(input?.name);
											setValue(input?.name || "", value, {
												shouldDirty: true,
											});
											onChange?.(value);
										}}
										onBlur={input?.onBlur}
										placeholderText={placeholderText}
										tabIndex={tabIndex}
										minDate={minDate}
										maxDate={maxDate}
									/>
								);
							}}
						/>
					)}
					rules={rules}
				/>
				<ErrorMessage field={name} errors={errors} />
			</Col>
		</FormGroup>
	);
};
export default memo(HookFormDatePickerCommon);
